"use strict";
/**
 * Text Management Service
 * Contains core business logic for text operations
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TextManagementService = void 0;
const textValidation_1 = require("../../rules/textValidation");
/**
 * Text Management Service Class
 */
class TextManagementService {
    /**
     * Validate text creation request
     */
    validateCreateTextRequest(request) {
        const errors = [];
        const warnings = [];
        // Basic validation
        if (!request.content?.trim()) {
            errors.push("Content is required");
        }
        if (!request.language) {
            errors.push("Language is required");
        }
        if (!request.level) {
            errors.push("Level is required");
        }
        if (!request.authorId) {
            errors.push("Author ID is required");
        }
        if (errors.length > 0) {
            return { isValid: false, errors, warnings };
        }
        // Content validation
        const textValidation = (0, textValidation_1.validateText)(request.content, request.level, request.textSize || "SHORT", request.language);
        errors.push(...textValidation.errors);
        warnings.push(...textValidation.warnings);
        // Text size validation for level
        const textSize = request.textSize || "SHORT";
        if (!(0, textValidation_1.isValidTextSizeForLevel)(request.level, textSize)) {
            errors.push(`Text size ${textSize} is not appropriate for level ${request.level}`);
        }
        return {
            isValid: errors.length === 0,
            errors,
            warnings,
        };
    }
    /**
     * Validate text update request
     */
    validateUpdateTextRequest(currentText, updateRequest) {
        const errors = [];
        const warnings = [];
        // If content is being updated, validate it
        if (updateRequest.content !== undefined) {
            const content = updateRequest.content.trim();
            if (!content) {
                errors.push("Content cannot be empty");
            }
            else {
                // Use current or updated level/textSize for validation
                const level = updateRequest.level || currentText.level;
                const textSize = updateRequest.textSize || currentText.textSize;
                const textValidation = (0, textValidation_1.validateText)(content, level, textSize, currentText.language);
                errors.push(...textValidation.errors);
                warnings.push(...textValidation.warnings);
            }
        }
        // If level or textSize is being updated, validate combination
        if (updateRequest.level !== undefined ||
            updateRequest.textSize !== undefined) {
            const level = updateRequest.level || currentText.level;
            const textSize = updateRequest.textSize || currentText.textSize;
            if (!(0, textValidation_1.isValidTextSizeForLevel)(level, textSize)) {
                errors.push(`Text size ${textSize} is not appropriate for level ${level}`);
            }
        }
        return {
            isValid: errors.length === 0,
            errors,
            warnings,
        };
    }
    /**
     * Calculate text statistics
     */
    calculateTextStatistics(content, language) {
        const validation = (0, textValidation_1.validateText)(content, "INTERMEDIATE", "MEDIUM", language);
        return {
            wordCount: validation.stats.wordCount,
            sentenceCount: validation.stats.sentenceCount,
            estimatedReadingTime: validation.stats.estimatedReadingTime,
            difficultyScore: Math.min(10, Math.max(1, Math.round(validation.stats.wordCount / 50))),
            averageWordsPerSentence: validation.stats.avgWordsPerSentence,
        };
    }
    /**
     * Check if user can access a text
     */
    checkTextAccess(text, userId, _userSubscription) {
        // Public texts are accessible to everyone
        if (text.isPublic && text.isApproved) {
            return { hasAccess: true };
        }
        // Author can always access their own texts
        if (userId && text.authorId === userId) {
            return { hasAccess: true };
        }
        // Private texts require author access
        if (text.isPrivate) {
            if (!userId || text.authorId !== userId) {
                return {
                    hasAccess: false,
                    reason: "This is a private text",
                };
            }
            return { hasAccess: true };
        }
        // Unapproved texts
        if (!text.isApproved) {
            if (!userId || text.authorId !== userId) {
                return {
                    hasAccess: false,
                    reason: "This text is pending approval",
                };
            }
            return { hasAccess: true };
        }
        return { hasAccess: true };
    }
    /**
     * Build text search filters
     */
    buildTextFilters(options) {
        const filters = {
            language: options.language,
            level: options.level,
            textSize: options.textSize,
            difficulty: options.difficulty,
            topic: options.topic,
            search: options.search,
            authorId: options.authorId,
            isPublic: options.isPublic,
            offset: options.offset,
            limit: options.limit,
            sortBy: options.sortBy === "date" ? "createdAt" : options.sortBy,
            sortOrder: options.sortOrder,
            includePrivate: options.includePrivate || false,
            includeUnapproved: options.includeUserTexts || false,
        };
        return filters;
    }
    /**
     * Calculate text popularity score
     */
    calculatePopularityScore(favoritesCount = 0, progressCount = 0, averageRating = 0, viewsCount = 0) {
        const favoritesWeight = 10;
        const progressWeight = 5;
        const ratingWeight = 3;
        const viewsWeight = 1;
        return (favoritesCount * favoritesWeight +
            progressCount * progressWeight +
            averageRating * ratingWeight +
            viewsCount * viewsWeight);
    }
    /**
     * Generate text summary
     */
    generateTextSummary(content, maxLength = 150) {
        if (content.length <= maxLength) {
            return content;
        }
        // Find the last complete sentence within the limit
        const truncated = content.substring(0, maxLength);
        const lastSentenceEnd = Math.max(truncated.lastIndexOf("."), truncated.lastIndexOf("!"), truncated.lastIndexOf("?"));
        if (lastSentenceEnd > maxLength * 0.7) {
            return content.substring(0, lastSentenceEnd + 1);
        }
        // Fallback to word boundary
        const lastSpace = truncated.lastIndexOf(" ");
        if (lastSpace > maxLength * 0.7) {
            return content.substring(0, lastSpace) + "...";
        }
        return truncated + "...";
    }
    /**
     * Suggest appropriate text sizes for a level
     */
    getRecommendedTextSizes(level) {
        const sizeValidation = (0, textValidation_1.validateTextSizeForLevel)(level, "SHORT"); // Get validation object
        return sizeValidation.allowedSizes;
    }
    /**
     * Calculate estimated study time
     */
    calculateEstimatedStudyTime(text) {
        const stats = this.calculateTextStatistics(text.content, text.language);
        // Base reading time
        const readingTime = stats.estimatedReadingTime;
        // Study time varies by level (beginners need more time)
        const studyMultipliers = {
            BEGINNER: 3,
            ELEMENTARY: 2.5,
            INTERMEDIATE: 2,
            UPPER_INTERMEDIATE: 1.8,
            ADVANCED: 1.5,
            PROFICIENT: 1.2,
        };
        const studyMultiplier = studyMultipliers[text.level];
        const studyTime = Math.round(readingTime * studyMultiplier);
        // Dictation time (typically longer than reading)
        const dictationTime = Math.round(readingTime * 1.5);
        return {
            readingTime,
            studyTime,
            dictationTime,
        };
    }
}
exports.TextManagementService = TextManagementService;
//# sourceMappingURL=textManagementService.js.map